import * as React from "react";
import { useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  makeStyles,
  tokens,
  Toaster,
  useToastController,
  useId,
  Toast,
  ToastTitle,
  ToastBody,
} from "@fluentui/react-components";
import NavigationSidebar from "./NavigationSidebar";
import { ribbonService } from "../../services/ribbon.service";
import { ExcelService } from "../../services/excel.service";
import { bulkReviewSheet } from "../../services/bulk-review.service";
import { useWorkflowIntelligence } from "../../contexts/WorkflowIntelligenceContext";
import { UserRole } from "../../types/UserRole";
import { PENDING_NAVIGATION_KEY } from "../../actions";

/**
 * Styles for the main layout container
 */
const useStyles = makeStyles({
  container: {
    display: "flex",
    height: "100vh",
    width: "100%",
    overflow: "hidden",
  },
  content: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

/**
 * Main layout component that provides the overall structure for the application
 * Includes:
 * - Navigation sidebar (collapsible)
 * - Main content area (rendered via React Router's Outlet)
 * - Global Toaster (used by ribbon-triggered fire-and-toast flows)
 */
const MainLayout: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { spreadsheetId, userRole, isWorkbookSignedOff, isStandalone } = useWorkflowIntelligence();

  // Toaster mounted at root so any route can surface a toast. Used today by
  // the Sign Off Current Sheet ribbon flow; future ribbon actions can dispatch
  // through here too.
  const toasterId = useId("wfi-toaster");
  const { dispatchToast } = useToastController(toasterId);

  // Keep current context values reachable from the (stable) event listener
  // below without re-subscribing on every render. The listener is fired
  // imperatively from Excel's ribbon, so we just read the latest values
  // at dispatch time.
  const stateRef = useRef({ spreadsheetId, userRole, isWorkbookSignedOff });
  stateRef.current = { spreadsheetId, userRole, isWorkbookSignedOff };

  // Listen for custom navigation events from keyboard shortcuts / ribbon buttons.
  // Dispatch closeAllPopups before navigating so any open dialog is dismissed —
  // covers both real route changes AND same-route clicks (where useLocation alone
  // wouldn't detect anything to react to).
  //
  // Cold-start drain: on the very first ribbon click (add-in pane wasn't open),
  // showAsTaskpane() returns before React has mounted, so the corresponding
  // navigateTo event fires before this listener is attached. actions/index.ts
  // ALSO stashes the target path in sessionStorage; we drain that here on
  // mount so the user gets to the right place on the first click instead of
  // needing a second click. The live listener below clears the stash on the
  // hot-path so a subsequent MainLayout remount can't replay a stale path.
  React.useEffect(() => {
    const handleNavigate = (event: CustomEvent<string>) => {
      sessionStorage.removeItem(PENDING_NAVIGATION_KEY);
      window.dispatchEvent(new Event("closeAllPopups"));
      navigate(event.detail);
    };

    window.addEventListener("navigateTo", handleNavigate as EventListener);

    const pending = sessionStorage.getItem(PENDING_NAVIGATION_KEY);
    if (pending) {
      sessionStorage.removeItem(PENDING_NAVIGATION_KEY);
      window.dispatchEvent(new Event("closeAllPopups"));
      navigate(pending);
    }

    return () => {
      window.removeEventListener("navigateTo", handleNavigate as EventListener);
    };
  }, [navigate]);

  // Also dispatch closeAllPopups whenever the route actually changes — covers
  // sidebar clicks (which call navigate() directly without going through the
  // navigateTo event) and any other path-change source.
  React.useEffect(() => {
    window.dispatchEvent(new Event("closeAllPopups"));
  }, [location.pathname]);

  // Fire-and-toast: sign off the active sheet when the ribbon button fires
  // the signOffCurrentSheet event. No confirmation dialog — stakeholders
  // wanted a single click. Failures and edge cases are surfaced via toast.
  // Skipped in standalone — the event is only ever fired from the Excel
  // ribbon, which doesn't exist here, and the handler calls Excel-only
  // methods (getActiveWorksheetName, ribbon updates) that would blow up.
  React.useEffect(() => {
    if (isStandalone) return;

    const handleSignOffCurrentSheet = async () => {
      const { spreadsheetId, userRole, isWorkbookSignedOff } = stateRef.current;

      const showError = (title: string, body?: string) =>
        dispatchToast(
          <Toast>
            <ToastTitle>{title}</ToastTitle>
            {body && <ToastBody>{body}</ToastBody>}
          </Toast>,
          { intent: "error", timeout: 5000 },
        );

      const showSuccess = (title: string, body?: string) =>
        dispatchToast(
          <Toast>
            <ToastTitle>{title}</ToastTitle>
            {body && <ToastBody>{body}</ToastBody>}
          </Toast>,
          { intent: "success", timeout: 4000 },
        );

      const showInfo = (title: string, body?: string) =>
        dispatchToast(
          <Toast>
            <ToastTitle>{title}</ToastTitle>
            {body && <ToastBody>{body}</ToastBody>}
          </Toast>,
          { intent: "info", timeout: 4000 },
        );

      if (!spreadsheetId) {
        showError("Workbook still loading", "Try again in a moment.");
        return;
      }
      if (isWorkbookSignedOff) {
        showError("Workbook is signed off", "This workbook is locked and can't be edited.");
        return;
      }
      if (userRole === UserRole.User) {
        showError(
          "Sign-off not available for your role",
          "Set your role to Preparer, Reviewer, or Partner in Settings.",
        );
        return;
      }

      try {
        const activeSheetName = await ExcelService.getActiveWorksheetName();
        const result = await bulkReviewSheet(spreadsheetId, activeSheetName, userRole);

        if (result.statusItemsUpdated > 0) {
          showSuccess(
            `Signed off "${activeSheetName}"`,
            `${result.statusItemsUpdated} status item${result.statusItemsUpdated === 1 ? "" : "s"} updated.`,
          );
          // Broadcast so any open status-item list refreshes, then update
          // ribbon indicator + tab colours so the new state is visible across
          // the addin and Excel itself.
          window.dispatchEvent(new CustomEvent("statusItemsChanged"));
          await ribbonService.syncAllWorksheetTabColors();
          await ribbonService.updateRibbonStatusIndicator();
        } else if (result.errors.length > 0) {
          // Most common error: "No status items found on sheet X" — info-level
          // because nothing actually went wrong, the sheet just has nothing
          // to sign off.
          showInfo(`Nothing to sign off on "${activeSheetName}"`, result.errors[0]);
        } else {
          showInfo(
            `"${activeSheetName}" already signed off`,
            "All status items on this sheet are at or past your review level.",
          );
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        showError("Sign-off failed", message);
      }
    };

    window.addEventListener("signOffCurrentSheet", handleSignOffCurrentSheet);
    return () => {
      window.removeEventListener("signOffCurrentSheet", handleSignOffCurrentSheet);
    };
  }, [dispatchToast, isStandalone]);

  return (
    <div className={styles.container}>
      <NavigationSidebar />
      <main className={styles.content}>
        <Outlet />
      </main>

      <Toaster toasterId={toasterId} position="top" />
    </div>
  );
};

export default MainLayout;
